import Container from 'atoms/Container';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import BrowsingItem from 'molecules/BrowsingItem/BrowsingItem';
import dynamic from 'next/dynamic';
import BrowsingGalleryView from 'organisms/BrowsingGalleryView';
import BrowsingHeader from 'organisms/BrowsingHeader';
import PortfolioWrapper from 'organisms/Portfolio/PortfolioWrapper';
import React, { useCallback, useEffect, useMemo } from 'react';
import { OfferingType } from 'stores/networkInterfaces';
import { getOfferingFields, getOfferingTypeStr } from 'stores/offeringUtils';
import { useStore } from 'stores/store';
import { IAggRequest, IFilter, IFilterOptionsItem, IOfferingInfo } from 'utils/globalTypes';
import usePageView from 'utils/hooks/usePageView';
import { useSearchApi } from 'utils/hooks/useSearchApi';
import { greedyOptions } from 'utils/mapOptions';
import { navigateToOffering } from 'utils/navigateUtils';
import { ISearch } from 'utils/networkRequests';
import {
  AGGREGATIONS,
  FILTERS,
  getDateFilter,
  getDateQueryFilter,
  getEnumValueFilter,
  getRangeFilter,
  getRangeQueryFilter
} from 'utils/query';

import s from './MarketplaceWrapper.module.scss';

const MapWrapper = dynamic(() => import('molecules/MapWrapper'), { ssr: false });

const aggregations = {
  asset_type: AGGREGATIONS.asset_type,
  sponsor_id: AGGREGATIONS.sponsor_id,
  offering_type: AGGREGATIONS.offering_type,
  t12_yield: AGGREGATIONS.t12_yield,
  implied_cap_rate: AGGREGATIONS.implied_cap_rate,
  changed_at: AGGREGATIONS.changed_at
} as IAggRequest;

const filterOptions: IFilterOptionsItem[] = [
  FILTERS.asset_type,
  FILTERS.sponsor_id,
  getDateFilter('Date', 'changed_at'),
  getEnumValueFilter('Offering Type', 'offering_type', OfferingType, getOfferingTypeStr)
];

const sortOptions = [
  {
    value: 'implied_cap_rate',
    name: 'Implied Cap'
  },
  {
    value: 't12_yield',
    name: 'Cash Yield'
  },
  {
    value: 'sponsor_id',
    name: 'Sponsor'
  },
  {
    value: 'changed_at',
    name: 'Last Updated'
  },
  {
    value: 'posted_at',
    name: 'Recently Posted'
  }
];

const getSortOptions = (key: string) => {
  switch (key) {
    case 'changed_at':
      return { id: 'changed_at', desc: true };
    case 'posted_at':
      return { id: 'posted_at', desc: true };
    case 'implied_cap_rate':
      return { id: 'implied_cap_rate', desc: true };
    case 't12_yield':
      return { id: 't12_yield', desc: true };
    case 'sponsor_id':
      return { id: 'sponsor_id', desc: false };
  }
};

const updateFilters = (filters?: IFilter[]) => {
  const outFilter = filters
    ? filters
        ?.map((filter) => {
          if (filter?.field === 'posted_at') {
            return getDateQueryFilter('posted_at', filter.value);
          } else if (filter?.field === 'changed_at') {
            return getDateQueryFilter('changed_at', filter.value);
          } else if (filter?.field === 'offering_type') {
            return { offering_type: filter.value };
          } else if (filter?.field === 'asset_type') {
            return { asset_type: filter.value };
          } else if (filter?.field === 'sponsor_id') {
            return { sponsor_id: filter.value };
          } else if (filter?.field === 'implied_cap_rate') {
            return getRangeQueryFilter('implied_cap_rate', filter.value);
          } else if (filter?.field === 't12_yield') {
            return getRangeQueryFilter('t12_yield', filter.value);
          }
        })
        ?.filter((filter) => !!filter)
    : [];

  return Object.assign({}, ...outFilter);
};

const MarketplaceWrapper = ({
  requestCmd,
  isPortfolio = false
}: {
  requestCmd?: string;
  isPortfolio?: boolean;
}) => {
  const store = useStore();

  const {
    isAuthenticated,
    isApproved,
    isManagement,
    isMobile,
    isLoading,
    isSearching,
    items,
    promotion,
    activeOrders,
    queryReady,
    query,
    onSetOffset,
    onSortOrder,
    onSearch,
    setFilters,
    setQueryUrl
  } = useSearchApi({
    requestCmd: requestCmd || 'searchOfferings',
    limit: 50,
    updateFilters
  });

  const { mapView, onCloseMap } = usePageView({
    cookieName: 'assetBrowsingView'
  });

  const showMapView = useMemo(
    () => mapView && !isPortfolio && isApproved,
    [isApproved, isPortfolio, mapView]
  );

  useEffect(() => {
    if (queryReady && showMapView) {
      store.MarketplaceStore.setAllAssets(requestCmd || 'searchOfferings').catch((error) => {
        console.warn('All assets query failed', error);
        // ignore the error
      });
    }
  }, [store.MarketplaceStore, requestCmd, showMapView, queryReady]);

  useEffect(() => {
    store.UIStore.setSearchStatus(
      store.MarketplaceStore.getOfferingSearchTerm,
      store.MarketplaceStore.getIsFiltered,
      isSearching,
      true
    );
  }, [store.UIStore, store.MarketplaceStore, isSearching]);

  const onNavigate = useCallback(async (offering?: IOfferingInfo) => {
    await navigateToOffering(offering?.offering_id);
  }, []);

  const setSortField = useCallback(
    (key: string) => {
      onSortOrder(getSortOptions(key));
    },
    [onSortOrder]
  );

  const onHeaderSearch = useCallback(
    (search?: ISearch) => {
      setQueryUrl({
        term: store.MarketplaceStore.getOfferingSearchTerm,
        filters: store.MarketplaceStore.getFilters,
        sortField: query?.order_by_field,
        sortDesc: query?.order_by_desc
      });

      store.MarketplaceStore.clearLocationFilter();
      onSearch(search);
    },
    [onSearch, query?.order_by_desc, query?.order_by_field, setQueryUrl, store.MarketplaceStore]
  );

  const sortOrder = useMemo(
    () => ({ id: query?.order_by_field, desc: query?.order_by_desc }),
    [query]
  );

  if (isLoading || !queryReady) {
    return null;
  }

  return (
    <PortfolioWrapper
      isEmpty={isPortfolio && !items?.data.length}
      title="Once you will add an offering to your watchlist, you'll see them here"
      description="Currently you are not watching any offering"
    >
      <div className={cn(s.MarketplaceWrapper__header, { isPortfolio })}>
        {isApproved && (
          <BrowsingHeader
            requestCmd={requestCmd}
            showTableView={false}
            showBreadcrumbs={isPortfolio}
            showSwitchBar={!isPortfolio}
            showOfferingSearch={!isPortfolio}
            showFilters={!isPortfolio}
            mapView={mapView}
            total={items?.total}
            onSearch={onHeaderSearch}
            setFilters={setFilters}
            setSortField={setSortField}
            sortOptions={sortOptions}
            sortOrder={sortOrder}
            aggregations={aggregations}
            filterOptions={filterOptions}
          />
        )}
      </div>
      <div className={cn(s.MarketplaceWrapper, { isPortfolio }, { isMapView: mapView })}>
        {showMapView && (
          <Container narrowPadding fullWidth isColumns>
            {!isMobile && (
              <section className={s.MarketplaceWrapper__list}>
                <BrowsingGalleryView
                  items={items?.data}
                  activeOrders={activeOrders}
                  offset={query?.offset}
                  limit={query?.limit}
                  total={items?.total}
                  onSetOffset={onSetOffset}
                  onNavigate={onNavigate}
                  getFields={getOfferingFields}
                />
              </section>
            )}
            <section className={s.MarketplaceWrapper__map}>
              <MapWrapper
                items={store.MarketplaceStore.allOfferings || items}
                mapOptions={greedyOptions}
                poiComponent={BrowsingItem}
                poiProps={{
                  getFields: getOfferingFields,
                  onNavigate,
                  isApproved,
                  isAuthenticated,
                  isManagement,
                  isPreviewMode: true,
                  isPOI: true
                }}
                containerStyle={{ height: '100%' }}
                onClose={onCloseMap}
                // setGeoFilter={onGeoSearch}
              />
            </section>
          </Container>
        )}
        {!showMapView && (
          <Container narrowPadding fullWidth>
            <section className={s.MarketplaceWrapper__gallery}>
              <BrowsingGalleryView
                items={items?.data}
                promotion={promotion}
                activeOrders={activeOrders}
                offset={query?.offset}
                limit={query?.limit}
                total={items?.total}
                onSetOffset={onSetOffset}
                onNavigate={onNavigate}
                getFields={getOfferingFields}
              />
            </section>
          </Container>
        )}
      </div>
    </PortfolioWrapper>
  );
};

export default observer(MarketplaceWrapper);
